/* eslint-disable react/prop-types */
import { Descriptions, Typography } from 'antd';
import { type TFunction, useTranslation } from 'react-i18next';
import { type BasicProLayoutSpecsFragment } from '../../../../../../api/fragments/BasicProLayoutSpecs';
import {
    ApplyBasicProLayoutOnRouterDocument,
    type ApplyBasicProLayoutOnRouterMutation,
    type ApplyBasicProLayoutOnRouterMutationVariables,
} from '../../../../../../api/mutations/applyBasicProLayoutOnRouter';
import { type BasicProLayoutSettings } from '../../../../../../api/types';
import CheckboxField from '../../../../../../components/fields/CheckboxField';
import SelectField from '../../../../../../components/fields/SelectField';
import renderBooleanIcon from '../../../../../../utilities/renderBooleanIcon';
import { type LayoutImplementation } from './types';

const implementation: LayoutImplementation<BasicProLayoutSpecsFragment, BasicProLayoutSettings> = {
    getTitle: (t: TFunction) => t('routerDetails:layouts.basicPro.title'),
    ViewPanelComponent: ({ layout }) => {
        const { t } = useTranslation(['routerDetails']);

        return (
            <>
                <Typography.Paragraph>{t('routerDetails:layouts.basicPro.description')}</Typography.Paragraph>
                <Descriptions column={2} size="small" bordered>
                    <Descriptions.Item label={t('routerDetails:layouts.basicPro.contentView.withSignInButton')}>
                        {renderBooleanIcon(layout.withSignInButton)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('routerDetails:layouts.basicPro.contentView.layoutMode')}>
                        {t(`routerDetails:layouts.basicPro.options.layoutMode.${layout.layoutMode}`)}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('routerDetails:layouts.basicPro.contentView.navigationTheme')}>
                        {t(`routerDetails:layouts.basicPro.options.navigationTheme.${layout.navigationTheme}`)}
                    </Descriptions.Item>
                </Descriptions>
            </>
        );
    },
    FormComponent: () => {
        const { t } = useTranslation(['routerDetails']);

        return (
            <>
                <CheckboxField
                    {...t('routerDetails:layouts.fields.withSignInButton', { returnObjects: true })}
                    name="settings.withSignInButton"
                />
                <SelectField
                    {...t('routerDetails:layouts.fields.layoutMode', { returnObjects: true })}
                    name="settings.layoutMode"
                    options={[
                        { value: 'side', label: t('routerDetails:layouts.basicPro.options.layoutMode.side') },
                        { value: 'mix', label: t('routerDetails:layouts.basicPro.options.layoutMode.mix') },
                    ]}
                    showSearch
                />
                <SelectField
                    {...t('routerDetails:layouts.fields.navigationTheme', { returnObjects: true })}
                    name="settings.navigationTheme"
                    options={[
                        { value: 'light', label: t('routerDetails:layouts.basicPro.options.navigationTheme.light') },
                        { value: 'dark', label: t('routerDetails:layouts.basicPro.options.navigationTheme.dark') },
                    ]}
                    showSearch
                />
            </>
        );
    },
    applyRouter: (apolloClient, routerId, settings) =>
        apolloClient.mutate<ApplyBasicProLayoutOnRouterMutation, ApplyBasicProLayoutOnRouterMutationVariables>({
            mutation: ApplyBasicProLayoutOnRouterDocument,
            variables: { routerId, settings },
        }),
    defaultSettings: { withSignInButton: false, layoutMode: 'mix', navigationTheme: 'light' },
    getInitialValues: layout => ({
        withSignInButton: layout.withSignInButton,
        layoutMode: layout.layoutMode,
        navigationTheme: layout.navigationTheme,
    }),
};

export default implementation;
